import { useMemo } from 'react'
import { useActiveOrders } from '@/hooks/commerce/useActiveOrders'
import { hasBrandingConfig, resolveBrandingTag } from '@/utils/branding'

import settings from '@/settings/public'

const appCode = settings.app.code ?? ''
const hasBranding = hasBrandingConfig(appCode)

/**
 * Derives the eligible full-branding tag from the shared active-orders cache
 * (QUERY_KEYS.activeOrders, dehydrated from SSR). Single source for "is full
 * branding active" — consumed by AppInitializer (loading gate + useBranding) and
 * by anything that must defer to full branding (e.g. team personalization,
 * onboarding suppression). Because it observes the shared orders entry, a
 * purchase invalidating ['orders'] re-derives the tag automatically.
 *
 * Gate with `enabled` from the caller (identity/login state) — this hook must not
 * read identity itself. Returns tag null when the app has no branding config.
 */
export const useEligibleBrandingTag = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { data: orders, isPending } = useActiveOrders({
    enabled: enabled && hasBranding,
  })

  const eligibleBrandingTag = useMemo(
    () => (orders ? resolveBrandingTag(orders, appCode) : null),
    [orders],
  )

  return { eligibleBrandingTag, isLoading: isPending, hasBranding }
}
